import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { currentLimits } from '#~/api/billing/limits.js';
import { toHexFromId } from '#~/api/index.js';
import { config } from '#~/config/config.js';
import { Button } from '@studio/design-system/button';
import { Icon } from '@studio/design-system/icon';
import { usePrompt } from '@studio/design-system/prompt';
import * as Select from '@studio/design-system/select';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { AgentRegistryCreateInviteResponse } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import { AgentRegistryMembershipRole } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useCallback, useEffect, useState } from 'react';
import { post } from '../../../notifications/index.js';
import { RoundedProfile } from './components.jsx';
import { RoleTranslationMap } from './constants.js';
import type { User } from './types.js';

// Equivilent to Go base64 url encoding
// - Replaces '+' with '-'
// - Replaces '/' with '_'
// - Removes trailing '='
const urlifyBase64 = (string_: string): string => {
  return string_.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** InviteByLink component. */
export const InviteByLink = ({
  onCreateLink,
  onDestroyLink,
  openInvite,
  runVerified
}: {
  onCreateLink: (permission: User['permission']) => Promise<string>;
  onDestroyLink: () => Promise<void>;
  openInvite: T_Maybe<AgentRegistryCreateInviteResponse>;
  /** Runs a link creation now, or holds it until the sender verifies their email. */
  runVerified: (proceed: () => unknown) => Promise<void>;
}) => {
  const prompt = usePrompt();
  const [loadedValue, setLoadedValue] = useState(false);

  const { track } = useAnalytics();

  const [value, setValue] = useState<User['permission']>(
    RoleTranslationMap[AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_PRODUCER]
  );

  useEffect(() => {
    if (!openInvite || loadedValue) {
      return;
    }

    setValue(
      RoleTranslationMap[
        openInvite?.role ?? AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_PRODUCER
      ]!
    );

    setLoadedValue(true);
  }, [openInvite, setLoadedValue]);

  const copyLink = useCallback(() => {
    if (!openInvite) {
      return;
    }

    const inviteId = urlifyBase64(toHexFromId(openInvite.id)!);
    const redemptionToken = urlifyBase64(toHexFromId(openInvite.redemptionToken)!);

    const link = `${config.frontendUrl}/invite/${inviteId}/${redemptionToken}`;

    void navigator.clipboard
      .writeText(link)
      .then(() => {
        track(EVENT_NAMES.SHARE_EXPIRING_LINK, EVENT_CATEGORIES.SHARING, {
          inviteId,
          link,
          redemptionToken
        });
        post({
          title: 'Link Copied to your clipboard',
          message: 'Share link to invite users to your studio.',
          severity: 'success'
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }, [openInvite]);

  const handleCreateLink = useLatestFn(async () => {
    if (!value) {
      return;
    }

    await onCreateLink(value);
  });

  const handleDestroyLink = async () => {
    await onDestroyLink();
  };

  const handleUpdateLink = async () => {
    await handleDestroyLink();
    await handleCreateLink();
  };

  const canSetPermissions = currentLimits?.limits?.canSetCollaboratorPermissions;

  return (
    <div className='flex items-center'>
      <div className='flex grow items-center gap-x-12'>
        <RoundedProfile className='drop-shadow-sm'>
          <Icon className='-rotate-45 text-lg' icon='link' />
        </RoundedProfile>
        {openInvite ? (
          <div className='flex flex-col gap-4'>
            <div>Anyone with this link</div>
            <button
              className='inline text-left text-primary underline'
              onClick={copyLink}
              type='button'
            >
              Copy link
            </button>
          </div>
        ) : (
          <span>Invite users with a link</span>
        )}
      </div>
      {openInvite ? (
        <div className='w-auto shrink'>
          <Select.Root
            onValueChange={(v) => {
              if (v === 'delete') {
                prompt.confirm({
                  title: 'Delete Link',
                  message: (
                    <div>
                      <p>Are you sure you want to delete this link?</p>
                      <p>
                        It will be permanently disabled and users will no longer be able to sign up.
                      </p>
                    </div>
                  ),
                  okay: { variant: 'danger', label: 'Delete Link' },

                  onConfirm: (answer) => {
                    if (answer) {
                      handleDestroyLink();
                    }
                  }
                });
              } else {
                // Changing the role re-creates the link, so hold the whole swap: never delete without re-creating.
                void runVerified(() => {
                  setValue(v as User['permission']);
                  void handleUpdateLink();
                });
              }
            }}
            value={value}
            variant='light'
          >
            <Select.Trigger className='bg-transparent'>
              <Select.Value />
            </Select.Trigger>
            <Select.Content align='end' position='popper' side='bottom'>
              {canSetPermissions && (
                <>
                  <Select.Item value='viewer'>Can join as a Viewer</Select.Item>
                  <Select.Item value='producer'>Can join as Producer</Select.Item>
                  <Select.Item value='admin'>Can join as Admin</Select.Item>
                </>
              )}
              <Select.Item className='h-auto px-0 text-error hover:text-white' value='delete'>
                <div className='flex flex-col gap-4 px-12 py-8'>
                  <div>Delete this link</div>
                  <div className='text-muted'>This link will no longer work.</div>
                </div>
              </Select.Item>
            </Select.Content>
          </Select.Root>
        </div>
      ) : (
        <Button
          className='mr-2 border-primary text-primary'
          onClick={(): void => {
            void runVerified(() => handleCreateLink());
          }}
          size='lg'
          variant='secondary'
        >
          Create Link
        </Button>
      )}
    </div>
  );
};
